import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import {
  catchError,
  filter,
  from,
  mergeMap,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TagModule } from 'primeng/tag';

import {
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  ActionButton,
  StatusFilterComponent,
} from '@app/shared/components/status-filter/status-filter.component';
import {
  ConfirmationButton,
  ConfirmationDialogComponent,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';

import { PermissionService } from '@app/core/services/permission.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  CARRIER_ACCOUNT_HEALTH_STATUS,
  CARRIER_COLUMNS,
  CARRIER_SEARCH_CONFIG,
  getHealthStatusDisplayText,
  getStatusDisplayText,
  getStatusSeverity,
} from './carrier.config';
import { CarrierService } from './services/carrier.service';
import {
  ConnectProviderRequestDTO,
  LogisticsProviderAccountDTO,
  LogisticsProviderPageRequestDTO,
  PageInfoLogisticsProviderAccountDTO,
} from '@app/api/after-sales-service';

@Component({
  selector: 'app-carrier',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    TagModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './carrier.component.html',
  styleUrl: './carrier.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarrierComponent implements OnInit, OnDestroy {
  // Pagination
  first = 0;
  rows = 20;

  // Configuration
  searchFormConfig = CARRIER_SEARCH_CONFIG;
  defaultColumns = computed((): TableColumn[] => CARRIER_COLUMNS);
  flexibleColumns = computed((): TableColumn[] => []);

  // Data signals
  dataSource = signal<
    (LogisticsProviderAccountDTO & { health?: string | null })[]
  >([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Search parameters
  searchParams = signal<LogisticsProviderPageRequestDTO>({
    providerName: undefined,
    code: undefined,
    name: undefined,
    status: undefined,
    page: 0,
    size: 20,
  });

  // Button configuration base
  private readonly primaryButtonBase: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  // Secondary button configuration for column config
  secondaryButton = computed(() => ({
    label: '',
    severity: 'success' as const,
    outlined: true,
    show: true,
    icon: 'view_column',
  }));

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // Confirmation dialog state
  showConfirmationDialog = signal(false);
  confirmationTitle = signal('');
  confirmationMessage = signal('');
  confirmationButtons = signal<ConfirmationButton[]>([]);
  selectedCarrierForAction = signal<LogisticsProviderAccountDTO | null>(null);
  currentAction = signal<'delete' | 'status' | null>(null);

  private readonly destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly carrierService = inject(CarrierService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  private readonly permissionsLoaded = toSignal(
    this.permissionService.permissionsLoaded$,
    { initialValue: false },
  );

  // Permission flags - using menu codes: settings.logistics.carrier_management.view/create/edit/delete/approve
  canRead = computed(() => {
    this.permissionsLoaded();
    return this.permissionService.canDoAction(
      'settings.logistics.carrier_management.view',
    );
  });
  canCreate = computed(() => {
    this.permissionsLoaded();
    return this.permissionService.canDoAction(
      'settings.logistics.carrier_management.create',
    );
  });
  canUpdate = computed(() => {
    this.permissionsLoaded();
    return this.permissionService.canDoAction(
      'settings.logistics.carrier_management.edit',
    );
  });
  canDelete = computed(() => {
    this.permissionsLoaded();
    return this.permissionService.canDoAction(
      'settings.logistics.carrier_management.delete',
    );
  });
  canApprove = computed(() => {
    this.permissionsLoaded();
    return this.permissionService.canDoAction(
      'settings.logistics.carrier_management.approve',
    );
  });

  ngOnInit(): void {
    this.loadCarriers();
    this.setupRouterListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupRouterListener(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationEnd) => {
        if (event.url.includes('/configuration/carrier')) {
          this.loadCarriers();
        }
      });
  }

  private fetchHealthStatusForRows(
    rows: (LogisticsProviderAccountDTO & { health?: string | null })[],
  ): void {
    const idsToFetch = rows
      .map((r) => r.id)
      .filter((id): id is number => typeof id === 'number');

    if (idsToFetch.length === 0) return;

    from(idsToFetch)
      .pipe(
        mergeMap(
          (id) =>
            this.carrierService.checkHealthAccount(id).pipe(
              catchError(() => of(CARRIER_ACCOUNT_HEALTH_STATUS.UNKNOWN)),
              mergeMap((health) => {
                this.dataSource.update((current) =>
                  current.map((row) =>
                    row.id === id ? { ...row, health } : row,
                  ),
                );
                return of(null);
              }),
            ),
          5,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  getHealthSeverity(
    health: CARRIER_ACCOUNT_HEALTH_STATUS,
  ): 'success' | 'warning' | 'danger' | 'secondary' | 'info' {
    if (!health) return 'secondary';

    switch (health) {
      case CARRIER_ACCOUNT_HEALTH_STATUS.CONNECTED:
        return 'success';
      case CARRIER_ACCOUNT_HEALTH_STATUS.SUSPENDED:
        return 'warning';
      case CARRIER_ACCOUNT_HEALTH_STATUS.UNAUTHORIZED:
        return 'info';
      case CARRIER_ACCOUNT_HEALTH_STATUS.UNREACHABLE:
        return 'danger';
      case CARRIER_ACCOUNT_HEALTH_STATUS.UNKNOWN:
        return 'secondary';
    }
  }

  private loadCarriers(): void {
    this.loading.set(true);

    const searchParams = this.searchParams();

    this.carrierService
      .searchCarriers(searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: PageInfoLogisticsProviderAccountDTO) => {
          const pageRows = response.pageContent || [];

          this.dataSource.set(pageRows);
          this.totalRecords.set(response.totalElements || 0);
          this.loading.set(false);

          this.fetchHealthStatusForRows(pageRows);
        },
        error: () => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'carrier.messages.error.failed_to_load_carriers',
            ),
          });
        },
      });
  }

  onSearch(searchData: SearchFormData): void {
    this.first = 0;

    this.searchParams.set({
      ...this.searchParams(),
      providerName: (searchData?.['providerName'] as string) || undefined,
      code: (searchData?.['accountCode'] as string) || undefined,
      name: (searchData?.['accountName'] as string) || undefined,
      status: (searchData?.['status'] as string) || undefined,
      page: 0,
    });

    this.loadCarriers();
  }

  onReset(): void {
    this.first = 0;

    this.searchParams.set({
      providerName: undefined,
      code: undefined,
      name: undefined,
      status: undefined,
      page: 0,
      size: this.rows,
    });

    this.loadCarriers();
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.rows = event.rows;

    this.searchParams.set({
      ...this.searchParams(),
      page: event.first / event.rows,
      size: event.rows,
    });

    this.loadCarriers();
  }

  // Status helpers
  getStatusSeverity(
    status: string,
  ): 'success' | 'warning' | 'danger' | 'secondary' {
    return getStatusSeverity(status);
  }

  getStatusDisplayText(status: string): string {
    return getStatusDisplayText(status, this.translateService);
  }

  getHealthStatusDisplayText(health: CARRIER_ACCOUNT_HEALTH_STATUS): string {
    return getHealthStatusDisplayText(health, this.translateService);
  }

  onAdd(): void {
    if (!this.canCreate()) {
      this.showNoPermissionWarning();
      return;
    }

    this.router.navigate(['/configuration/carrier/add']);
  }

  onView(carrier: LogisticsProviderAccountDTO): void {
    if (!this.canRead()) {
      this.showNoPermissionWarning();
      return;
    }

    this.router.navigate(['/configuration/carrier/view', carrier.id]);
  }

  onEdit(carrier: LogisticsProviderAccountDTO): void {
    if (!this.canUpdate()) {
      this.showNoPermissionWarning();
      return;
    }

    this.router.navigate(['/configuration/carrier/edit', carrier.id]);
  }

  onDelete(carrier: LogisticsProviderAccountDTO): void {
    if (!this.canDelete()) {
      this.showNoPermissionWarning();
      return;
    }

    this.selectedCarrierForAction.set(carrier);
    this.currentAction.set('delete');
    this.confirmationTitle.set(
      this.translateService.instant('common.confirmation.title'),
    );
    this.confirmationMessage.set(
      this.translateService.instant('carrier.messages.confirmation.delete', {
        name: carrier.name || carrier.code || '',
      }),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onStatusChange(
    carrier: LogisticsProviderAccountDTO,
    newStatus: string,
  ): void {
    if (!this.canApprove()) {
      this.showNoPermissionWarning();
      return;
    }

    const isActivating = newStatus === 'ACTIVE';
    this.selectedCarrierForAction.set(carrier);
    this.currentAction.set('status');
    this.confirmationTitle.set(
      this.translateService.instant('common.confirmation.title'),
    );
    this.confirmationMessage.set(
      this.translateService.instant(
        isActivating
          ? 'carrier.messages.confirmation.activate'
          : 'carrier.messages.confirmation.deactivate',
        { name: carrier.name || carrier.code || '' },
      ),
    );
    this.confirmationButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          isActivating
            ? 'common.table.actions.activate'
            : 'common.table.actions.deactivate',
        ),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.showConfirmationDialog.set(true);
  }

  onConfirmationDialogAction(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
      return;
    }

    if (action !== 'confirm') {
      this.closeConfirmationDialog();
      return;
    }

    const carrier = this.selectedCarrierForAction();
    const actionType = this.currentAction();

    if (!carrier || !actionType) {
      this.closeConfirmationDialog();
      return;
    }

    if (actionType === 'delete') {
      this.handleDeleteCarrier(carrier);
      return;
    }

    if (actionType === 'status') {
      const nextStatus = carrier.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      this.handleStatusChange(carrier, nextStatus);
      return;
    }

    this.closeConfirmationDialog();
  }

  private handleDeleteCarrier(carrier: LogisticsProviderAccountDTO): void {
    this.loading.set(true);
    this.carrierService.deleteCarrier(carrier.id).subscribe({
      next: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'carrier.messages.success.carrier_deleted',
          ),
        });

        if (this.dataSource().length === 1 && this.first > 0) {
          this.first = Math.max(0, this.first - this.rows);
          this.searchParams.set({
            ...this.searchParams(),
            page: this.first / this.rows,
          });
        }

        this.closeConfirmationDialog();
        this.loadCarriers();
      },
      error: (error) => {
        console.error('Error deleting carrier:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'carrier.messages.error.failed_to_delete_carrier',
          ),
        });
      },
    });
  }

  private handleStatusChange(
    carrier: LogisticsProviderAccountDTO,
    newStatus: string,
  ): void {
    this.loading.set(true);

    const payload: Partial<ConnectProviderRequestDTO> = {
      providerCode: carrier.providerCode,
      status: newStatus,
    };

    this.carrierService.updateCarrier(carrier.id, payload).subscribe({
      next: () => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            newStatus === 'ACTIVE'
              ? 'carrier.messages.success.carrier_activated'
              : 'carrier.messages.success.carrier_deactivated',
          ),
        });

        this.closeConfirmationDialog();
        this.loadCarriers();
      },
      error: (error) => {
        console.error('Error updating carrier status:', error);
        this.loading.set(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'carrier.messages.error.failed_to_update_status',
          ),
        });
      },
    });
  }

  private closeConfirmationDialog(): void {
    this.showConfirmationDialog.set(false);
    this.selectedCarrierForAction.set(null);
    this.currentAction.set(null);
  }

  private showNoPermissionWarning(): void {
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('common.messages.warning'),
      detail: this.translateService.instant('common.permissions.no_permission'),
    });
  }

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
